import { Check } from 'lucide-react';
import { IDEBadge } from '@/components/shared/extensions';
import {
  AGENTMESH_RISK_BUCKETS,
  AGENTMESH_SCAN_COVERAGES,
  RISK_ESTIMATE_STATE_FILTER_VALUES,
  type AgentmeshRiskBucket,
  type AgentmeshScanCoverage,
  type ExtensionSource,
} from '@kirin/api-client/contracts/extensions';
import { SourceCell, type SourceType } from '@/components/shared/extensions';
import {
  riskFilterOptionLabel,
  RiskEstimateCell,
  ScanStatusBadge,
  ScanStatusCell,
  scanStatusFilterOptionLabel,
} from '@/components/shared/extensions';
import { AgentmeshTagBadge } from '@/components/shared/agentmesh/AgentmeshTagBadge';

interface FilterOptionProps {
  value: string;
  isSelected: boolean;
}

export function ExtensionSourceFilterOption({
  value,
  isSelected,
}: FilterOptionProps) {
  return (
    <div className="flex items-center gap-2">
      {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
      <SourceCell source={value as SourceType} showTooltip={false} />
    </div>
  );
}

export function ExtensionIDEFilterOption({
  value,
  isSelected,
}: FilterOptionProps) {
  return (
    <div className="flex items-center gap-2">
      {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
      <IDEBadge sources={[value as ExtensionSource]} />
    </div>
  );
}

const isRiskBucket = (value: string): value is AgentmeshRiskBucket =>
  (AGENTMESH_RISK_BUCKETS as readonly string[]).includes(value);

const isScanCoverage = (value: string): value is AgentmeshScanCoverage =>
  (AGENTMESH_SCAN_COVERAGES as readonly string[]).includes(value);

/**
 * Risk multiselect option (KIR-5208): a verdict renders as its cell's pill;
 * "No verdict yet" (and a value saved before KIR-5208) and "Not applicable"
 * as their cells do.
 */
export function RiskEstimateFilterOption({
  value,
  isSelected,
}: FilterOptionProps) {
  return (
    <div className="flex items-center gap-2">
      {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
      {isRiskBucket(value) ? (
        <AgentmeshTagBadge tag={value} />
      ) : value === RISK_ESTIMATE_STATE_FILTER_VALUES.NO_VERDICT ||
        value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_ASSESSED ? (
        <span className="text-xs">
          <RiskEstimateCell riskEstimate={null} />
        </span>
      ) : value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_SCANNED_BUILT_IN ? (
        <span className="text-xs">
          <RiskEstimateCell riskEstimate={null} notApplicable />
        </span>
      ) : (
        <span className="text-xs">{riskFilterOptionLabel(value)}</span>
      )}
    </div>
  );
}

/**
 * Scan status multiselect option (KIR-5208): each value renders exactly as
 * its cell does, "Not applicable" included.
 */
export function ScanStatusFilterOption({
  value,
  isSelected,
}: FilterOptionProps) {
  return (
    <div className="flex items-center gap-2">
      {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
      {isScanCoverage(value) ? (
        <ScanStatusBadge coverage={value} />
      ) : value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_SCANNED_BUILT_IN ? (
        <span className="text-xs">
          <ScanStatusCell scanStatus={null} isBuiltIn />
        </span>
      ) : (
        <span className="text-xs">{scanStatusFilterOptionLabel(value)}</span>
      )}
    </div>
  );
}
